/**
 * 路由跳转方法，该方法相对于直接使用uni.xxx的好处是使用更加简单快捷
 * 并且带有路由拦截功能
 */

interface RouteConfig {
  type: string;
  url: string;
  delta: number;
  params: Record<string, any>;
  animationType: string;
  animationDuration: number;
  intercept: boolean;
}

class Router {
  private config: RouteConfig;

  constructor() {
    this.config = {
      type: 'navigateTo',
      url: '',
      delta: 1,
      params: {},
      animationType: 'pop-in',
      animationDuration: 300,
      intercept: false,
    };
    this.route = this.route.bind(this);
  }

  /**
   * 补全 url 的根路径
   */
  private addRootPath(url: string): string {
    return url[0] === '/' ? url : `/${url}`;
  }

  /**
   * 合并参数到 url
   */
  private mixinParam(url: string, params: Record<string, any> = {}): string {
    url = url && this.addRootPath(url);
    let query = '';
    if (/.*\/.*\?.*=.*/.test(url)) {
      query = uni.$u.queryParams(params, false);
      return url + `&${query}`;
    }
    query = uni.$u.queryParams(params);
    return url + query;
  }

  /**
   * 路由跳转主方法
   */
  async route(options: string | RouteOptions, params?: Record<string, any>): Promise<void> {
    let mergeConfig: RouteConfig = { ...this.config };
    if (typeof options === 'string') {
      mergeConfig.url = this.mixinParam(options, params);
      mergeConfig.type = 'navigateTo';
    } else {
      mergeConfig = uni.$u.deepMerge(this.config, options);
      if (options.url != null) {
        mergeConfig.url = this.mixinParam(options.url, options.params);
      }
    }
    if (mergeConfig.url === uni.$u.page()) return;
    if (params && params.intercept) {
      mergeConfig.intercept = params.intercept;
    }
    // @ts-ignore
    mergeConfig.params = params;
    mergeConfig = uni.$u.deepMerge(this.config, mergeConfig);
    this.openPage(mergeConfig);
  }

  /**
   * 执行路由跳转
   */
  private openPage(config: RouteConfig): void {
    const { url, type, delta } = config;
    switch (type) {
      case 'navigateTo':
      case 'to':
        uni.navigateTo({ url });
        break;
      case 'redirectTo':
      case 'redirect':
        uni.redirectTo({ url });
        break;
      case 'switchTab':
      case 'tab':
        uni.switchTab({ url });
        break;
      case 'reLaunch':
      case 'launch':
        uni.reLaunch({ url });
        break;
      case 'navigateBack':
      case 'back':
        uni.navigateBack({ delta });
        break;
    }
  }
}

/**
 * 路由跳转方法类型声明
 */
export interface RouteOptions {
  url?: string;
  type?:
    | 'navigateTo'
    | 'to'
    | 'redirectTo'
    | 'redirect'
    | 'switchTab'
    | 'tab'
    | 'reLaunch'
    | 'launch'
    | 'navigateBack'
    | 'back';
  delta?: number;
  params?: Record<string, any>;
  animationType?: string;
  animationDuration?: number;
  intercept?: boolean;
  [key: string]: any;
}

export type RouteFunction = (options: string | RouteOptions, params?: Record<string, any>) => Promise<void>;

const route: RouteFunction = new Router().route;

export default route;
